/**
 * -----------------------------------------------------------------------------
 * NCJ COMPONENT: VIN Check Product Renderer
 * FILE: ncj-vin-check-product-renderer.js
 * PURPOSE: Handles DOM interaction (variants, Add to Cart, toggles) for the
 *          natively rendered VIN check results HTML. Addon toggle logic is
 *          delegated to NCJUtils (Smart Reuse).
 * AUTHOR: No Code Jungle (NCJ)
 * LAST MODIFIED: 2026-03-05
 * -----------------------------------------------------------------------------
 */

function initVinCheckResults(json, container) {
  var wrapper = container.querySelector('.ncj-vin-check-results-wrapper');
  if (!wrapper) return;

  var vin = json.vin || "";
  var othersPrice = json.othersPrice || "";
  var reviewCount = json.reviewCount || "";
  var isConfirmableAsteriskFree = json.valid === "true" && json.message === "Yes, we can provide this COC";

  // 1. Populate dynamic JSON values
  var vinSpan = wrapper.querySelector('[data-vin-text]');
  if (vinSpan) vinSpan.textContent = vin;

  var reviewSpan = wrapper.querySelector('[data-review-count]');
  if (reviewSpan) reviewSpan.textContent = reviewCount;

  var othersSpan = wrapper.querySelector('[data-others-price]');
  if (othersSpan) othersSpan.textContent = othersPrice;

  // 2. Asterisk Logic
  if (!isConfirmableAsteriskFree) {
    var asteriskSpan = wrapper.querySelector('[data-asterisk-span]');
    if (asteriskSpan) asteriskSpan.textContent = "*";

    var footerNote = wrapper.querySelector('[data-footer-note]');
    if (footerNote) footerNote.style.display = "block";
  }

  // 3. Availability Text Logic
  var availTextElem = wrapper.querySelector('[data-availability-text]');
  if (availTextElem) {
    if (json.valid === "true") {
      var availSrc = wrapper.querySelector('[data-text-available]');
      if (availSrc) availTextElem.textContent = availSrc.textContent;
    } else if (json.valid === "pending_verification") {
      var likelySrc = wrapper.querySelector('[data-text-likely]');
      if (likelySrc) availTextElem.textContent = likelySrc.textContent;
    }
  }

  var businessDaysText = json.businessDays || "business days";
  var businessDaysPlusShippingText = json.businessDaysPlusShipping || "business days + shipping";

  window.vinCheckTranslations = window.vinCheckTranslations || {};
  window.vinCheckTranslations.vin = vin;

  window.vinCheckTranslations.processingTimes = {
    "Original COC": json.processingTimeCocOriginal || "",
    "PDF Only": json.processingTimePdfOnly || ""
  };
  window.vinCheckTranslations.businessDays = businessDaysText;
  window.vinCheckTranslations.businessDaysPlusShipping = businessDaysPlusShippingText;

  // 4. Initialize Variant Selector
  setupVariantSelector(wrapper, vin);

  // 5. Initialize Addon Toggle — delegate to NCJUtils (Smart Reuse)
  if (window.NCJUtils && window.NCJUtils.initExistingAddonToggle) {
    window.NCJUtils.initExistingAddonToggle(wrapper);
  }
  alignAddonWithVariants(wrapper);
  window.addEventListener("resize", function () { alignAddonWithVariants(wrapper); });

  // 6. Init Discount countdown block
  renderDiscountBlock(wrapper);
}

function setupVariantSelector(wrapper, vin) {
  var buttons = wrapper.querySelectorAll(".variant-btn");
  var priceDisplay = wrapper.querySelector("[data-current-price]");
  var priceDisplay2 = wrapper.querySelector("[data-current-price-2]");
  var priceDisplayVat = wrapper.querySelector("[data-current-price-vat]");
  var addToCartButton = wrapper.querySelector(".add-to-cart-btn");
  var addonRow = wrapper.querySelector(".addon-toggle-row");
  var addonInput = wrapper.querySelector(".addon-toggle-input");
  var addonTitleEl = wrapper.querySelector("[data-addon-toggle-title]");

  function selectVariantButton(btn) {
    buttons.forEach(function (b) { b.classList.remove("active"); });
    btn.classList.add("active");

    var variantId = btn.dataset.id;
    var variantSku = btn.dataset.sku || "";
    var variantPrice = btn.dataset.price;
    var variantPriceVat = btn.dataset.priceVat;
    var variantTitle = btn.dataset.title || "";

    if (priceDisplay) priceDisplay.textContent = "€" + variantPrice;
    if (priceDisplay2) priceDisplay2.textContent = "€" + variantPrice;
    if (priceDisplayVat) priceDisplayVat.textContent = "€" + variantPriceVat;

    // Show the correct pre-rendered pill for the variant
    var pills = wrapper.querySelectorAll('.processing-time-variant');
    if (pills.length > 0) {
      pills.forEach(function (pill) {
        if (pill.getAttribute('data-variant-root-id') === variantId.toString()) {
          pill.style.display = 'block';
        } else {
          pill.style.display = 'none';
        }
      });
    }

    // Legacy pill logic
    var legacyInfoText = wrapper.querySelector('.ncj-info-text-legacy');
    if (legacyInfoText) {
      var processingTime = (window.vinCheckTranslations && window.vinCheckTranslations.processingTimes && window.vinCheckTranslations.processingTimes[variantTitle]) || "";
      var bText = (window.vinCheckTranslations && window.vinCheckTranslations.businessDays) || "business days";
      var bpText = (window.vinCheckTranslations && window.vinCheckTranslations.businessDaysPlusShipping) || "business days + shipping";

      if (variantTitle.includes("PDF Only") || variantSku.includes("-2")) {
        legacyInfoText.textContent = processingTime + " " + bText;
      } else {
        legacyInfoText.textContent = processingTime + " " + bpText;
      }
    }

    // Show/hide addon toggle based on variant (Smart Reuse: use NCJUtils._updateAddonLabel)
    if (addonRow && addonInput && addonTitleEl) {
      if (variantSku.includes("-1") || variantTitle.includes("Original COC")) {
        addonRow.style.visibility = "visible";
        addonRow.style.pointerEvents = "auto";
        if (window.NCJUtils && window.NCJUtils._updateAddonLabel) {
          window.NCJUtils.loadAddonProduct().then(function (result) {
            window.NCJUtils._updateAddonLabel(addonInput, addonTitleEl, result.priceText);
          });
        }
      } else {
        addonRow.style.visibility = "hidden";
        addonRow.style.pointerEvents = "none";
        addonInput.checked = false;
        if (window.NCJUtils && window.NCJUtils._updateAddonLabel) {
          window.NCJUtils.loadAddonProduct().then(function (result) {
            window.NCJUtils._updateAddonLabel(addonInput, addonTitleEl, result.priceText);
          });
        }
      }
      alignAddonWithVariants(wrapper);
    }

    // Initialize Delivery Estimator for this context
    var estimator = wrapper.querySelector('.ncj-delivery-estimator');
    var initEstimator = function () {
      if (estimator && typeof window.initNcjDeliveryEstimator === 'function') {
        try {
          estimator.setAttribute('data-sku', variantSku);
          window.initNcjDeliveryEstimator(estimator);
        } catch (e) {
          /* delivery estimator init failed silently */
        }
      }
    };

    if (typeof window.initNcjDeliveryEstimator === 'function') {
      initEstimator();
    } else {
      var scriptTag = wrapper.querySelector('script[src*="ncj-delivery-estimator.js"]');
      if (scriptTag && !document.querySelector('script[src="' + scriptTag.src + '"][data-ncj-injected]')) {
        var newScript = document.createElement('script');
        newScript.src = scriptTag.src;
        newScript.defer = true;
        newScript.setAttribute('data-ncj-injected', 'true');
        newScript.onload = initEstimator;
        document.head.appendChild(newScript);
      } else {
        var attempts = 0;
        var interval = setInterval(function () {
          attempts++;
          if (typeof window.initNcjDeliveryEstimator === 'function') {
            initEstimator();
            clearInterval(interval);
          }
          if (attempts > 50) clearInterval(interval);
        }, 100);
      }
    }

    // Add to cart
    addToCartButton.onclick = function () {
      addToCartButton.disabled = true;

      var items = [];

      // 1. Addon FIRST — COC will display on top in cart
      var addonState = window.NCJUtils ? window.NCJUtils.getAddonState() : null;
      if (addonState && addonState.checked && addonState.variantId) {
        var addonProps = {};
        if (addonState.vendor) {
          addonProps["Brand"] = addonState.vendor;
        }
        items.push({
          id: parseInt(addonState.variantId),
          quantity: 1,
          properties: addonProps
        });
      }

      // 2. COC SECOND — displayed on top in cart
      items.push({
        id: parseInt(variantId),
        quantity: 1,
        properties: { VIN: vin }
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function () {
          var currentLocalePrefix = (window.location.pathname.match(/^\/(bg|cs|da|de|el|en|es|fr|it|nl|pl|pt|pt-pt|ro)(?=\/|$)/i) || [""])[0];

          if (window.isBoldUser) {
            window.location.href = currentLocalePrefix + "/cart";
          } else {
            window.location.href = currentLocalePrefix + "/checkout";
          }
        })
        .catch(function () {
          alert("Something went wrong. Please try again.");
          addToCartButton.disabled = false;
        });
    };
  }

  buttons.forEach(function (btn) {
    btn.addEventListener("click", function () { selectVariantButton(btn); });
  });

  var defaultButton = wrapper.querySelector(".variant-btn.active") || buttons[0];
  if (defaultButton) {
    selectVariantButton(defaultButton);
  }
}

function alignAddonWithVariants(wrapper) {
  var variantsEl = wrapper.querySelector(".variant-options");
  var addonRow = wrapper.querySelector(".addon-toggle-row");
  if (!variantsEl || !addonRow) return;

  var width = variantsEl.getBoundingClientRect().width;
  if (width > 0) {
    addonRow.style.width = width + "px";
  }
  addonRow.style.justifyContent = "flex-start";
  addonRow.style.alignSelf = "center";
}

function getRemainingDiscountTime() {
  var start = localStorage.getItem("discountBlockStartTime");
  if (!start) return 0;

  var now = Date.now();
  var elapsed = now - parseInt(start, 10);
  var duration = 30 * 60 * 1000;

  return Math.max(0, Math.floor((duration - elapsed) / 1000));
}

function hasPassedCooldownPeriod() {
  var last = localStorage.getItem("discountBlockLastShown");
  if (!last) return true;

  var now = Date.now();
  var elapsed = now - parseInt(last, 10);

  return elapsed > 24 * 60 * 60 * 1000;
}

function getDiscountConfig() {
  var config = window.vinCheckConfig || {};
  return {
    showDiscount: Boolean(config.showDiscount),
    code: (config.discountCode || "").trim()
  };
}

function startDiscountCountdown(remainingSeconds, countdownElement, blockElement) {
  var remaining = remainingSeconds;

  var interval = setInterval(function () {
    var minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
    var seconds = String(remaining % 60).padStart(2, '0');
    countdownElement.textContent = minutes + ":" + seconds;

    if (remaining <= 0) {
      clearInterval(interval);
      blockElement.style.display = 'none';
      localStorage.setItem("discountBlockLastShown", Date.now().toString());
      localStorage.removeItem("discountBlockStartTime");
    }

    remaining--;
  }, 1000);
}

function renderDiscountBlock(wrapper) {
  var discountConfig = getDiscountConfig();
  if (!discountConfig.showDiscount || !discountConfig.code) return;
  var code = discountConfig.code;

  var cooldownOver = hasPassedCooldownPeriod();
  if (!cooldownOver) return;

  if (!localStorage.getItem("discountBlockStartTime") && cooldownOver) {
    localStorage.setItem("discountBlockStartTime", Date.now().toString());
  }

  var remaining = getRemainingDiscountTime();
  if (remaining <= 0) return;

  var translations = window.vinCheckTranslations || {};
  var message = translations.discountMessage || "Discount code for your first order";
  var copiedText = translations.copiedText || "Copied!";

  var discountWrapper = document.createElement("div");
  discountWrapper.id = "discount-block";
  discountWrapper.innerHTML =
    '<div class="discount-inner">' +
    '<span id="discount-timer">00:00</span>' +
    '<span id="discount-message">' + message + '</span>' +
    '<div class="discount-code-container">' +
    '<span class="discount-code">' + code + '</span>' +
    '<span class="copy-icon-wrapper">' +
    '<svg class="copy-icon" width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" stroke="none" fill="#000000" opacity="0"/>' +
    '<g transform="matrix(1.43 0 0 1.43 12 12)">' +
    '<path style="stroke: none; stroke-width: 1; fill: rgb(0,0,0); opacity: 1;" transform=" translate(-8, -7.5)" d="M 2.5 1 C 1.675781 1 1 1.675781 1 2.5 L 1 10.5 C 1 11.324219 1.675781 12 2.5 12 L 4 12 L 4 12.5 C 4 13.324219 4.675781 14 5.5 14 L 13.5 14 C 14.324219 14 15 13.324219 15 12.5 L 15 4.5 C 15 3.675781 14.324219 3 13.5 3 L 12 3 L 12 2.5 C 12 1.675781 11.324219 1 10.5 1 Z M 2.5 2 L 10.5 2 C 10.78125 2 11 2.21875 11 2.5 L 11 10.5 C 11 10.78125 10.78125 11 10.5 11 L 2.5 11 C 2.21875 11 2 10.78125 2 10.5 L 2 2.5 C 2 2.21875 2.21875 2 2.5 2 Z M 12 4 L 13.5 4 C 13.78125 4 14 4.21875 14 4.5 L 14 12.5 C 14 12.78125 13.78125 13 13.5 13 L 5.5 13 C 5.21875 13 5 12.78125 5 12.5 L 5 12 L 10.5 12 C 11.324219 12 12 11.324219 12 10.5 Z"></path>' +
    '</g>' +
    '</svg>' +
    '<span class="copied-tooltip">' + copiedText + '</span>' +
    '</span>' +
    '</div>' +
    '</div>';

  var vinBox = wrapper.querySelector(".vin-product-box");
  if (vinBox) vinBox.appendChild(discountWrapper);

  var countdownEl = discountWrapper.querySelector("#discount-timer");
  startDiscountCountdown(remaining, countdownEl, discountWrapper);

  var copyIcon = discountWrapper.querySelector(".copy-icon");
  var copiedMsg = discountWrapper.querySelector(".copied-tooltip");

  if (copyIcon && copiedMsg) {
    copyIcon.addEventListener("click", function () {
      navigator.clipboard.writeText(code).then(function () {
        copiedMsg.classList.add("show");
        setTimeout(function () {
          copiedMsg.classList.remove("show");
        }, 2000);
      });
    });
  }
}
